import React, { useState } from 'react';
import { Eye, Sun, Moon, Glasses, ArrowRight } from './icons';
import { rgbToHex, hexToRgb } from '../utils/colors';
import {
  simulateColorBlindness,
  simulateEnvironment,
  COLOR_BLIND_LABELS,
  COLOR_BLIND_SHORT,
  ENVIRONMENT_LABELS,
  type ColorBlindType,
  type EnvironmentType,
} from '../utils/colorBlindness';

interface ColorEntry {
  hex: string;
  label: string;
}

interface Props {
  colors: ColorEntry[];
}

const VISION_TYPES: ColorBlindType[] = [
  'normal',
  'deuteranomaly', 'protanomaly', 'tritanomaly',
  'deuteranopia', 'protanopia', 'tritanopia',
  'achromatopsia',
];

const ENV_TYPES: { type: EnvironmentType; icon: React.ElementType }[] = [
  { type: 'none', icon: Eye },
  { type: 'bright-sunlight', icon: Sun },
  { type: 'low-light', icon: Moon },
  { type: 'blurred', icon: Glasses },
];

const ColorBlindnessSimulator: React.FC<Props> = ({ colors }) => {
  const [activeType, setActiveType] = useState<ColorBlindType>('normal');
  const [activeEnv, setActiveEnv] = useState<EnvironmentType>('none');

  return (
    <div className="ui-panel p-6">
      <div className="flex items-center gap-2 mb-4">
        <Eye size={18} className="text-brand-500" />
        <h3 className="text-base font-semibold text-ink">Vision Simulator</h3>
      </div>

      {/* Vision type selector */}
      <div className="mb-3">
        <div className="text-xs text-ink-muted font-medium uppercase tracking-wider mb-1.5">Color Vision</div>
        <div className="flex flex-wrap gap-1.5">
          {VISION_TYPES.map(type => (
            <button
              key={type}
              onClick={() => setActiveType(type)}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                activeType === type
                  ? 'bg-brand-600 text-white'
                  : 'bg-surface-sunken text-ink-secondary hover:bg-brand-50 hover:text-brand-600'
              }`}
            >
              {COLOR_BLIND_SHORT[type]}
            </button>
          ))}
        </div>
      </div>

      {/* Environment selector */}
      <div className="mb-4">
        <div className="text-xs text-ink-muted font-medium uppercase tracking-wider mb-1.5">Environment</div>
        <div className="flex gap-1.5">
          {ENV_TYPES.map(({ type, icon: Icon }) => (
            <button
              key={type}
              onClick={() => setActiveEnv(type)}
              aria-label={type === 'none' ? 'Normal environment' : ENVIRONMENT_LABELS[type]}
              aria-pressed={activeEnv === type}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                activeEnv === type
                  ? 'bg-amber-600 text-white'
                  : 'bg-surface-sunken text-ink-secondary hover:bg-amber-50 hover:text-amber-600'
              }`}
            >
              <Icon size={14} />
              <span className="hidden sm:inline">{type === 'none' ? 'Normal' : type === 'bright-sunlight' ? 'Sun' : type === 'low-light' ? 'Night' : 'Blur'}</span>
            </button>
          ))}
        </div>
      </div>

      <p className="text-xs text-ink-secondary mb-4">
        {COLOR_BLIND_LABELS[activeType]}
        {activeEnv !== 'none' && ` + ${ENVIRONMENT_LABELS[activeEnv]}`}
      </p>

      {/* Color swatches comparison */}
      <div className="grid grid-cols-1 gap-4">
        {colors.map(({ hex, label }) => {
          const rgb = hexToRgb(hex) ?? { r: 0, g: 0, b: 0 };
          const cbRgb = simulateColorBlindness(rgb.r, rgb.g, rgb.b, activeType);
          const envRgb = simulateEnvironment(cbRgb.r, cbRgb.g, cbRgb.b, activeEnv);
          const simHex = rgbToHex(envRgb.r, envRgb.g, envRgb.b);
          const same = hex.toUpperCase() === simHex.toUpperCase() && activeEnv !== 'blurred';

          return (
            <div key={hex} className="flex items-center gap-3">
              <div className="flex-1">
                <div className="text-xs text-ink-secondary mb-1">{label} — Original</div>
                <div
                  className="h-10 rounded-lg border border-surface-border flex items-center justify-end px-3"
                  style={{ background: hex }}
                >
                  <span
                    className="text-xs font-mono px-1.5 py-0.5 rounded"
                    style={{ background: 'rgba(0,0,0,0.35)', color: '#fff' }}
                  >
                    {hex.toUpperCase()}
                  </span>
                </div>
              </div>

              <ArrowRight size={18} className="text-ink-muted" />

              <div className="flex-1">
                <div className="text-xs text-ink-secondary mb-1">
                  {same ? 'No change' : 'Simulated'}
                </div>
                <div
                  className="h-10 rounded-lg border border-surface-border flex items-center justify-end px-3"
                  style={{
                    background: simHex,
                    filter: activeEnv === 'blurred' ? 'blur(2px)' : undefined,
                  }}
                >
                  <span
                    className="text-xs font-mono px-1.5 py-0.5 rounded"
                    style={{ background: 'rgba(0,0,0,0.35)', color: '#fff' }}
                  >
                    {simHex.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-ink-muted mt-4">
        Vision: Viénot 1999 + Machado 2009. Environment: approximate perceptual simulation.
      </p>
    </div>
  );
};

export default ColorBlindnessSimulator;
